import classNames from 'classnames';
import { isNull } from 'lodash/fp';
import React, { useCallback } from 'react';

import { DatasetFieldType, SingleDatasetFieldType } from '../../../datasets/store';
import { OverflowTooltip } from '../../../shared/tooltip';
import styles from './AIManager.module.scss';
import { DocumentQueryDB } from './store';
import { FieldValue } from '../../../datasets/instances/store';

interface HistoricalAIQueryReadOnlyProps {
    historicalQuery: DocumentQueryDB;
    relevantDocumentNames: { documentName: string; documentNameId: number | undefined; }[]
}

export const HistoricalAIQueryReadOnly: React.FC<HistoricalAIQueryReadOnlyProps> = ({ historicalQuery, relevantDocumentNames }) => {
    const { documentQueryId, question, defaultAnswer, percentage, guidanceNotes, totalQueries, correctQueries, isEdgeCase, displayQuestion, fieldType, documentNameIds } = historicalQuery;

    const getDefaultValue = useCallback((defaultAnswer: FieldValue, fieldType: SingleDatasetFieldType | null, dropdownListId: number | null, isMultiSelect: number, isEdgeCase: number): string | null => {
        if (isNull(fieldType) || ![DatasetFieldType.DROPDOWN, DatasetFieldType.TEXT, DatasetFieldType.LONGTEXT, DatasetFieldType.NUMBER].includes(fieldType) || isEdgeCase === 1) {
            return null;
        }
        return DatasetFieldType.DROPDOWN === fieldType ? (defaultAnswer as string[]).join('\n ') : `${defaultAnswer as string | number}`;
    }, []);

    return (
        <div className={styles.aiHistoricalQueryWrapper} id={`ai-query-read-only-${documentQueryId}`}>
            <div className={styles.aiHistoricalQuerySectionWrapper}>
                <div className={styles.aiReadOnlyDataPointWrapper}>
                    <div className={styles.aiDataPointLabel}>Question</div>
                    <div className={styles.aiDataPointText}>{question}</div>
                </div>
                <div className={styles.aiReadOnlyDataPointWrapper}>
                    <div className={styles.aiDataPointLabel}>Display Question</div>
                    <div className={classNames(styles.aiDataPointText, { [styles.aiDataPointDefaultText]: !displayQuestion })}>
                        {displayQuestion || 'None provided'}
                    </div>
                </div>
                <div className={styles.aiReadOnlyDataPointWrapper}>
                    <div className={styles.aiDataPointLabel}>Guidance Notes</div>
                    <div className={classNames(styles.aiDataPointText, { [styles.aiDataPointDefaultText]: !guidanceNotes })}>
                        {guidanceNotes || 'None provided'}
                    </div>
                </div>
                <div className={styles.aiReadOnlyDataPointWrapper}>
                    <div className={styles.aiDataPointLabel}>Default Answer</div>
                    <div className={classNames(styles.aiDataPointText, { [styles.aiDataPointDefaultText]: !defaultAnswer })}>
                        {defaultAnswer ? getDefaultValue(defaultAnswer, fieldType, null, 0, isEdgeCase) : 'None provided'}
                    </div>
                </div>
                <div className={styles.aiReadOnlyDataPointWrapper}>
                    <div className={styles.aiDataPointLabel}>
                        Relevant Document Names
                    </div>
                    {relevantDocumentNames
                        .filter(({ documentNameId }) => documentNameIds.includes(documentNameId!))
                        .map(({ documentNameId, documentName }) => <div key={documentNameId} className={styles.aiDataPointText}>{documentName}</div>)}
                </div>
                {isEdgeCase ? <div className={styles.aiReadOnlyDataPointWrapper}>
                    <div className={styles.aiDataPointLabel}>This query is / was an edge case.</div>
                </div> : null}
            </div>
            <div>
                {documentQueryId && isEdgeCase === 0 &&
                    <div className={styles.percentageWrapper}>
                        <div className={styles.percentageBar}>
                            <div className={styles.successPercentage} style={{ width: `${percentage}%` }} />
                        </div>
                        <div className={styles.successInfo}>
                            <div className={styles.successString}>
                                <OverflowTooltip overlayText={`Success Rate: ${correctQueries} / ${totalQueries}`} />
                            </div>
                            <div className={styles.displayPercentage}>{percentage}%</div>
                        </div>
                    </div>
                }
            </div>
        </div>
    );
};
